import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { ShadowBox, EmptyState } from '@unowmooc/react-ui-kit'

import Cell from './Cell'
import CellHead from './CellHead'
import CellDate from './CellDate'
import CellNumber from './CellNumber'
import CellAction from './CellAction'
import CellActionLink from './CellActionLink'
import RowSelectable from './RowSelectable'
import Rows from './Rows'
import Row from './Row'
import Loader from './Loader'

import styles from './styles.module.scss'

class DataGrid extends Component {
  get labels() {
    const { children } = this.props

    return React.Children.map(children, ({ props: { label } }) => label)
  }

  getRows(isSelectedRow, onSelectRow, dragging = false) {
    const { data, renderSelectionHandler, onReorder, children } = this.props

    return (
      <Rows
        rows={data}
        labels={this.labels}
        isSelectedRow={isSelectedRow}
        onSelectRow={renderSelectionHandler ? onSelectRow : undefined}
        withReorder={!!onReorder}
        withSelect={!!renderSelectionHandler}
        dragging={dragging}
      >
        {children}
      </Rows>
    )
  }

  render() {
    const { loading, data, children, onReorder, renderSelectionHandler, emptyStateMessage } = this.props

    const displayFetchLoader = loading && !Array.isArray(data)
    const displayEmptyState = !loading && Array.isArray(data) && data.length === 0

    return (
      <ShadowBox className={styles.shadowBox}>
        <RowSelectable
          render={({ onSelectRow, isSelectedRow, clearSelection, selectedRows }) => (
            <>
              <div className={styles.dataGrid} style={{ backgroundColor: 'white' }}>
                <Row className={styles.dataGridHeading}>
                  {renderSelectionHandler && <CellHead render={() => null} className={styles.dataGridHeadSelect} />}
                  {onReorder && <CellHead render={() => null} className={styles.dataGridHeadReorder} />}

                  {React.Children.map(children, ({ props: { label, align } }) => (
                    <CellHead align={align} render={() => label} />
                  ))}
                </Row>

                {data && (
                  <>
                    {onReorder ? (
                      <DragDropContext onDragEnd={onReorder}>
                        <Droppable droppableId="droppable">
                          {(droppableProvided, droppableSnapshot) => (
                            <div ref={droppableProvided.innerRef} className={styles.dataGridBody}>
                              {this.getRows(isSelectedRow, onSelectRow, droppableSnapshot.isDraggingOver)}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    ) : (
                      this.getRows(isSelectedRow, onSelectRow)
                    )}
                  </>
                )}
              </div>
              {displayEmptyState && <EmptyState className={styles.emptyState} message={emptyStateMessage} />}
              {displayFetchLoader && <Loader />}
              {renderSelectionHandler && renderSelectionHandler({ selectedRows, onClearSelection: clearSelection })}
            </>
          )}
        />
      </ShadowBox>
    )
  }
}

DataGrid.defaultProps = {
  loading: false,
  data: undefined,
  onReorder: undefined,
  renderSelectionHandler: undefined,
  emptyStateMessage: undefined,
}

DataGrid.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  loading: PropTypes.bool,
  onReorder: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }),
  ),
  renderSelectionHandler: PropTypes.func,
  emptyStateMessage: PropTypes.node,
}

DataGrid.DataGridCell = Cell
DataGrid.DataGridCellDate = CellDate
DataGrid.DataGridCellNumber = CellNumber
DataGrid.DataGridCellAction = CellAction
DataGrid.DataGridCellActionLink = CellActionLink

export {
  Cell as DataGridCell,
  CellDate as DataGridCellDate,
  CellNumber as DataGridCellNumber,
  CellAction as DataGridCellAction,
  CellActionLink as DataGridCellActionLink,
}

export default DataGrid
